import React, { useState, useEffect, useContext } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Button,
  Tooltip,
  Card,
  CardContent,
  Typography,
  useMediaQuery,
  useTheme,
  Divider,
  Box,
  CardActions,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import Grid2 from "@mui/material/Grid2";
import { searchUsers } from "../API/UsersApi";
import AuthContext from "../Context/AuthProvider";
import { useSnackbar } from "../Utility/UserMessageContext";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import IconButton from "@mui/material/IconButton";
import { getDisplayValue } from "../Utility/Utility";
import { CloseOutlined, CreateOutlined } from "@mui/icons-material";
import MenuBar from "../common/MenuBar";
import "../common/styles/CommonStyles.css"; // Import your custom styles
import GenericTextField from "../common/GenericTextField";
import HeaderValueDisplay from "../common/HeaderValueDisplay";
import GenericDatePicker from "../common/GenericDatePicker";
import GenericDropDown from "../common/GenericDropDown";
import GenericFileUploader from "../common/GenericFileUploader";
import { UserRolesConstants } from "../Utility/Constants";

function StaffSettings({ onNewUser, onViewDetails }) {
  const [pageSize, setPageSize] = useState(5);
  const [users, setUsers] = useState([]);
  const [showSpinner, setShowSpinner] = useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { auth } = useContext(AuthContext);
  const { showSnackbar } = useSnackbar();

  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
  }, [auth?.selectedFacility]); // Refetch when selected facility changes

  const isFacilityAdmin =
    auth?.roles?.includes(UserRolesConstants.FacilityAdmin) || false;

  const fetchUsers = async () => {
    setShowSpinner(true);
    try {
      const searchParams = {
        facilityId: auth?.selectedFacility?.id,
        roles: [], // Empty array to get all roles
      };

      const result = await searchUsers(auth, searchParams);
      let index = 1;
      if (result.isSuccess && Array.isArray(result.data)) {
        // Transform the API response to match our grid structure
        const transformedUsers = result.data.map((user) => ({
          serialNumber: index++,
          id: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          phone: user.mobileNo,
          email: user.emailId,
          roles: user.roles.map((role) => getDisplayValue(role)).join(", "), // Join roles into a string
          roles: user.roles.map((role) => getDisplayValue(role)).join(", "), // Join roles into a string
          // Add any other fields you need
        }));

        setUsers(transformedUsers);
      } else {
        showSnackbar("Failed to load users", "error");
        console.error("Error fetching users:", result);
      }
    } catch (error) {
      showSnackbar("Error loading users", "error");
      console.error("Exception while fetching users:", error);
    } finally {
      setShowSpinner(false);
    }
  };

  // Callback function to refresh users after adding a new one
  const handleUserAdded = () => {
    fetchUsers();
  };

  const columns = [
    {
      field: "serialNumber",
      headerName: "#",
      width: 50,
    },
    { field: "firstName", headerName: "First Name", width: 150 },
    { field: "lastName", headerName: "Last Name", width: 150 },
    { field: "roles", headerName: "Roles", width: 150 },
    { field: "roles", headerName: "Roles", width: 150 },
    { field: "phone", headerName: "Phone Number", width: 150 },
    { field: "email", headerName: "Email ID", width: 200 },

    // {
    //   field: "actions",
    //   headerName: "Actions",
    //   width: 120,
    //   sortable: false,
    //   renderCell: (params) => (
    //     <Tooltip title="View Details" arrow>
    //       <IconButton variant="outlined" onClick={() => onViewDetails(params.row)}>
    //         <EventNoteOutlinedIcon fontSize="small" />
    //       </IconButton>
    //     </Tooltip>
    //   ),
    // },
    // Action column removed as requested
  ];

  // Card view for small screens
  const renderCardView = () => {
    return (
      <Grid2 sx={{ px: 1 }}>
        {showSpinner ? (
          <Typography variant="body1" align="center" py={3}>
            Loading...
          </Typography>
        ) : users.length === 0 ? (
          <Typography variant="body1" align="center" py={3}>
            There are no users to display.
          </Typography>
        ) : (
          users.map((user) => (
            <Card
              key={user.id}
              variant="outlined"
              sx={{ mb: 2, borderRadius: 2 }}
            >
              <CardContent sx={{ padding: 2, borderRadius: 2 }}>
                <Typography variant="body1" fontWeight="bold">
                  {`${user.firstName || ""} ${user.lastName || ""}`}
                </Typography>

                <Divider sx={{ my: 1 }} />

                <Box sx={{ "& > div": { mb: 1 } }}>
                  {/* <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2">User ID:</Typography>
                    <Typography variant="body2">{user.id || "-"}</Typography>
                  </Box> */}

                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2">Role:</Typography>
                    <Typography variant="body2">{user.roles || "-"}</Typography>
                  </Box>

                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2">Phone:</Typography>
                    <Typography variant="body2">{user.phone || "-"}</Typography>
                  </Box>

                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2">Email:</Typography>
                    <Typography variant="body2">{user.email || "-"}</Typography>
                  </Box>
                </Box>

                {/* <CardActions sx={{ justifyContent: "flex-end",}}>
                  <Tooltip title="View Details">
                    <IconButton
                      onClick={() => onViewDetails(user)}
                      size="small"
                    >
                      <CreateOutlined />
                    </IconButton>
                  </Tooltip>
                </CardActions> */}
              </CardContent>
            </Card>
          ))
        )}
      </Grid2>
    );
  };

  return (
    <Grid2 container>
      {/* New User Action */}
      <Grid2 container size={12}>
        <Grid2 size={12}>
          <MenuBar
            newLabel="New User"
            showNew={true}
            onNew={() => onNewUser(handleUserAdded)}
            disabled={!isFacilityAdmin}
          />
        </Grid2>
      </Grid2>

      <Grid2
        xs={12}
        sx={{
          marginX: "5px",
          width: "100%",
          maxHeight: "calc(100vh - 170px)",
          overflowY: "auto",
        }}
      >
        {isSmallScreen ? (
          renderCardView()
        ) : (
          <DataGrid
            localeText={{ noRowsLabel: "There are no users to display." }}
            loading={showSpinner}
            slotProps={{
              loadingOverlay: {
                variant: "skeleton",
                noRowsVariant: "skeleton",
              },
            }}
            density="compact"
            rows={users}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5]}
            disableSelectionOnClick
            disableColumnMenu
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f5f5f5",
                fontWeight: "bold",
              },
              "& .MuiDataGrid-footerContainer": {
                justifyContent: "space-between",
              },
              width: "100%",
              "& .MuiTablePagination-toolbar": {
                minHeight: "unset",
                alignItems: "center",
                paddingTop: "0 !important",
                paddingBottom: "0 !important",
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                {
                  margin: 0,
                },
            }}
          />
        )}
      </Grid2>
    </Grid2>
  );
}

export default StaffSettings;
